/**
 * Authentication State Store (Zustand)
 *
 * Zustand is a lightweight state management library.
 * Why Zustand instead of Redux?
 * - Much simpler API (no actions, reducers, dispatch)
 * - Less boilerplate code
 * - Better TypeScript support
 * - Perfect for small-medium apps
 *
 * This store manages:
 * - Current user
 * - Authentication token
 * - Loading states
 * - Error states
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';
import { authApi } from '../services/api';

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setError: (error: string | null) => void;
  loadUserFromStorage: () => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

/**
 * Create Zustand store with persistence
 *
 * persist() middleware saves state to localStorage automatically.
 * When user refreshes page, state is restored from localStorage.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      /**
       * Set user and update authentication status
       */
      setUser: (user) => {
        set({
          user,
          isAuthenticated: user !== null,
          error: null,
        });
      },

      /**
       * Set authentication token
       */
      setToken: (token) => {
        if (token) {
          localStorage.setItem('auth_token', token);
        } else {
          localStorage.removeItem('auth_token');
        }
        set({ token, isAuthenticated: token !== null });
      },

      /**
       * Set error message
       */
      setError: (error) => {
        set({ error, isLoading: false });
      },

      /**
       * Clear error message
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Load user from localStorage on app startup
       *
       * This is called when the app first loads to restore authentication state.
       * Checks both domain cookies AND localStorage for token.
       * If token exists, we validate it with the backend.
       */
      loadUserFromStorage: async () => {
        const lsToken = localStorage.getItem('auth_token');
        const cookieToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('auth_token='))
          ?.split('=')[1];

        // The .hitsearch.biz cookie is the source of truth for cross-service
        // auth (nginx auth_request only reads the cookie). If localStorage
        // has a token but the cookie does not, the two have drifted out of
        // sync — treat localStorage as stale, otherwise the app will think
        // it is authenticated and bounce the user to services that will
        // reject them, producing infinite redirect loops.
        if (lsToken && !cookieToken) {
          console.warn('[AuthStore] localStorage token present but .hitsearch.biz cookie missing — treating localStorage as stale.');
          localStorage.removeItem('auth_token');
          set({ isAuthenticated: false, user: null, token: null });
          return;
        }

        let token = lsToken;
        if (cookieToken && !lsToken) {
          console.log('[AuthStore] Found token in domain cookie, syncing to localStorage');
          token = cookieToken;
          localStorage.setItem('auth_token', cookieToken);
        }

        if (!token) {
          set({ isAuthenticated: false, user: null, token: null });
          return;
        }

        set({ isLoading: true });

        try {
          // Validate token and get user profile
          const user = await authApi.getProfile();
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          // Token is invalid or expired
          localStorage.removeItem('auth_token');
          // Also clear domain cookie (MUST match all attributes from when cookie was set)
          document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.hitsearch.biz; secure; samesite=lax';
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: 'Session expired. Please login again.',
          });
        }
      },

      /**
       * Logout user
       *
       * Calls backend logout endpoint (for audit trail),
       * then clears local state and storage.
       */
      logout: async () => {
        try {
          await authApi.logout();
        } catch (error) {
          console.error('Logout error:', error);
          // Continue with local logout even if backend call fails
        }

        // Clear state
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        });

        // Clear storage
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');

        // Clear domain cookie (MUST match all attributes from when cookie was set)
        // secure and samesite flags MUST be included to match HTTPS cookies
        document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.hitsearch.biz; secure; samesite=lax';
      },
    }),
    {
      name: 'auth-storage',  // localStorage key
      partialize: (state) => ({
        // Only persist these fields (not loading/error states)
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
